import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import "./AdminDashboard.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5087";
const NEWSLETTER_STORAGE_KEY = "techxm_newsletter_subscribers";
const TESTIMONIALS_STORAGE_KEY = "techxm_testimonials";
const FAQ_STORAGE_KEY = "techxm_faqs";
const AUTH_TOKEN_KEY = "techxm_admin_token";

export default function AdminDashboard() {
  const [authUser, setAuthUser] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [authError, setAuthError] = useState("");
  const [isAuthed, setIsAuthed] = useState(false);
  const [authToken, setAuthToken] = useState("");
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [newProject, setNewProject] = useState({
    projectName: "",
    clientName: "",
    email: "",
    startDate: new Date().toISOString().split("T")[0],
    estimatedCompletion: "",
  });
  const [subscribers, setSubscribers] = useState([]);
  const [newsletterSubject, setNewsletterSubject] = useState("");
  const [newsletterMessage, setNewsletterMessage] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialForm, setTestimonialForm] = useState({
    content: "",
    author: "",
    position: "",
    avatar: "",
  });
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [faqForm, setFaqForm] = useState({ question: "", answer: "" });
  const [editingFaq, setEditingFaq] = useState(null);

  const handleLogin = (e) => {
    e.preventDefault();
    setAuthError("");

    const username = authUser.trim();
    const password = authPass;

    if (!username || !password) {
      setAuthError("Please enter your username and password.");
      return;
    }

    fetch(`${API_BASE_URL}/api/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ username, password }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const message = await parseApiError(response, "Login failed");
          throw new Error(message);
        }

        return response.json();
      })
      .then((data) => {
        if (!data?.token) {
          throw new Error("Login failed: token was not returned.");
        }

        sessionStorage.setItem(AUTH_TOKEN_KEY, data.token);
        setAuthToken(data.token);
        setIsAuthed(true);
        setAuthPass("");
      })
      .catch((error) => {
        console.error("Login error:", error);
        setAuthError(error.message || "Login failed.");
      });
  };

  const handleLogout = () => {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    setIsAuthed(false);
    setAuthToken("");
    setAuthUser("");
    setAuthPass("");
  };

  const loadSubscribers = useCallback(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(NEWSLETTER_STORAGE_KEY) || "[]");
      const normalized = Array.isArray(stored) ? stored : [];
      setSubscribers(normalized);
    } catch (error) {
      console.error("Failed to load newsletter subscribers:", error);
      setSubscribers([]);
    }
  }, []);

  const loadTestimonials = useCallback(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(TESTIMONIALS_STORAGE_KEY) || "[]");
      const normalized = Array.isArray(stored) ? stored : [];
      setTestimonials(normalized);
    } catch (error) {
      console.error("Failed to load testimonials:", error);
      setTestimonials([]);
    }
  }, []);

  const loadFaqs = useCallback(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(FAQ_STORAGE_KEY) || "[]");
      const normalized = Array.isArray(stored) ? stored : [];
      setFaqs(normalized);
    } catch (error) {
      console.error("Failed to load FAQs:", error);
      setFaqs([]);
    }
  }, []);

  const parseApiError = useCallback(async (response, fallbackMessage) => {
    try {
      const contentType = response.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const body = await response.json();
        if (body?.message) {
          return body.message;
        }
        if (body?.title) {
          return body.title;
        }
      }
    } catch (error) {
      console.error("Failed to parse API error response:", error);
    }

    return `${fallbackMessage} (HTTP ${response.status})`;
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/projects`);

      if (!response.ok) {
        const message = await parseApiError(response, "Failed to fetch projects");
        throw new Error(message);
      }

      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
      alert(error.message || "Error fetching projects from API");
    } finally {
      setLoading(false);
    }
  }, [parseApiError]);

  useEffect(() => {
    const storedToken = sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (storedToken) {
      setAuthToken(storedToken);
      setIsAuthed(true);
      fetchProjects();
      loadSubscribers();
      loadTestimonials();
      loadFaqs();
    }
  }, [fetchProjects, loadSubscribers, loadTestimonials, loadFaqs]);

  useEffect(() => {
    if (isAuthed) {
      fetchProjects();
      loadSubscribers();
      loadTestimonials();
      loadFaqs();
    }
  }, [isAuthed, fetchProjects, loadSubscribers, loadTestimonials, loadFaqs]);

  const createProject = async () => {
    if (!newProject.projectName || !newProject.clientName || !newProject.email || !newProject.estimatedCompletion) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        projectName: newProject.projectName,
        clientName: newProject.clientName,
        email: newProject.email,
        startDate: newProject.startDate,
        estimatedCompletion: newProject.estimatedCompletion,
        currentStep: 1,
        progress: 10,
      };

      const response = await fetch(`${API_BASE_URL}/api/projects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const message = await parseApiError(response, "Failed to create project");
        throw new Error(message);
      }

      alert("Project created successfully");

      setNewProject({
        projectName: "",
        clientName: "",
        email: "",
        startDate: new Date().toISOString().split("T")[0],
        estimatedCompletion: "",
      });

      await fetchProjects();
    } catch (error) {
      console.error("Error creating project:", error);
      alert(error.message || "Error creating project");
    } finally {
      setLoading(false);
    }
  };

  const updateProject = async () => {
    if (!selectedProject || !editForm) return;

    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/projects/${selectedProject.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(editForm),
      });

      if (!response.ok) {
        const message = await parseApiError(response, "Failed to update project");
        throw new Error(message);
      }

      alert("Project updated successfully");
      setSelectedProject(null);
      setEditForm(null);
      await fetchProjects();
    } catch (error) {
      console.error("Error updating project:", error);
      alert(error.message || "Error updating project");
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project? This cannot be undone.")) return;

    try {
      setLoading(true);

      const response = await fetch(`${API_BASE_URL}/api/projects/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (!response.ok) {
        const message = await parseApiError(response, "Failed to delete project");
        throw new Error(message);
      }

      alert("Project deleted.");
      if (selectedProject?.id === id) {
        setSelectedProject(null);
        setEditForm(null);
      }
      await fetchProjects();
    } catch (error) {
      console.error("Error deleting project:", error);
      alert(error.message || "Error deleting project");
    } finally {
      setLoading(false);
    }
  };

  const copySubscribers = async () => {
    if (!subscribers.length) {
      alert("No subscribers found yet.");
      return;
    }

    const emailList = subscribers.map((item) => item.email).join(", ");
    try {
      await navigator.clipboard.writeText(emailList);
      alert("Subscriber emails copied to clipboard.");
    } catch (error) {
      console.error("Clipboard copy failed:", error);
      alert("Unable to copy emails automatically. Please copy manually.");
    }
  };

  const sendNewsletterToAll = () => {
    if (!subscribers.length) {
      alert("No subscribers found yet.");
      return;
    }

    if (!newsletterSubject.trim() || !newsletterMessage.trim()) {
      alert("Please add both a subject and message.");
      return;
    }

    const bcc = subscribers.map((item) => item.email).join(",");
    const mailto = `mailto:?bcc=${encodeURIComponent(bcc)}&subject=${encodeURIComponent(
      newsletterSubject.trim()
    )}&body=${encodeURIComponent(newsletterMessage.trim())}`;

    window.location.href = mailto;
  };

  const saveTestimonials = (next) => {
    setTestimonials(next);
    localStorage.setItem(TESTIMONIALS_STORAGE_KEY, JSON.stringify(next));
  };

  const saveFaqs = (next) => {
    setFaqs(next);
    localStorage.setItem(FAQ_STORAGE_KEY, JSON.stringify(next));
  };

  const resetTestimonialForm = () => {
    setTestimonialForm({
      content: "",
      author: "",
      position: "",
      avatar: "",
    });
    setEditingTestimonial(null);
  };

  const resetFaqForm = () => {
    setFaqForm({ question: "", answer: "" });
    setEditingFaq(null);
  };

  const handleTestimonialSubmit = (e) => {
    e.preventDefault();
    if (!testimonialForm.content.trim() || !testimonialForm.author.trim() || !testimonialForm.position.trim()) {
      alert("Please add content, author, and position.");
      return;
    }

    const entry = {
      content: testimonialForm.content.trim(),
      author: testimonialForm.author.trim(),
      position: testimonialForm.position.trim(),
      avatar:
        testimonialForm.avatar.trim() ||
        "https://randomuser.me/api/portraits/lego/1.jpg",
    };

    if (editingTestimonial !== null) {
      const next = testimonials.map((item, index) =>
        index === editingTestimonial ? entry : item
      );
      saveTestimonials(next);
      resetTestimonialForm();
      return;
    }

    saveTestimonials([entry, ...testimonials]);
    resetTestimonialForm();
  };

  const startEditTestimonial = (index) => {
    const item = testimonials[index];
    if (!item) return;
    setEditingTestimonial(index);
    setTestimonialForm({
      content: item.content || "",
      author: item.author || "",
      position: item.position || "",
      avatar: item.avatar || "",
    });
  };

  const removeTestimonialEntry = (index) => {
    const next = testimonials.filter((_, i) => i !== index);
    saveTestimonials(next);
    if (editingTestimonial === index) {
      resetTestimonialForm();
    }
  };

  const handleFaqSubmit = (e) => {
    e.preventDefault();
    if (!faqForm.question.trim() || !faqForm.answer.trim()) {
      alert("Please add both a question and answer.");
      return;
    }

    const entry = {
      question: faqForm.question.trim(),
      answer: faqForm.answer.trim(),
    };

    if (editingFaq !== null) {
      const next = faqs.map((item, index) => (index === editingFaq ? entry : item));
      saveFaqs(next);
      resetFaqForm();
      return;
    }

    saveFaqs([entry, ...faqs]);
    resetFaqForm();
  };

  const startEditFaq = (index) => {
    const item = faqs[index];
    if (!item) return;
    setEditingFaq(index);
    setFaqForm({
      question: item.question || "",
      answer: item.answer || "",
    });
  };

  const removeFaqEntry = (index) => {
    const next = faqs.filter((_, i) => i !== index);
    saveFaqs(next);
    if (editingFaq === index) {
      resetFaqForm();
    }
  };

  const removeSubscriber = (email) => {
    const updated = subscribers.filter((item) => item.email !== email);
    setSubscribers(updated);
    localStorage.setItem(NEWSLETTER_STORAGE_KEY, JSON.stringify(updated));
  };

  if (!isAuthed) {
    return (
      <div className="admin-dashboard">
        <div className="admin-auth-card">
          <h1>Admin Access</h1>
          <p>Enter your admin credentials to continue.</p>
          <form className="admin-auth-form" onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Username"
              value={authUser}
              onChange={(e) => setAuthUser(e.target.value)}
              className="form-input"
              autoComplete="username"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={authPass}
              onChange={(e) => setAuthPass(e.target.value)}
              className="form-input"
              autoComplete="current-password"
              required
            />
            {authError ? <div className="auth-error">{authError}</div> : null}
            <button type="submit" className="primary-button">
              Sign In
            </button>
          </form>
          <div className="admin-auth-note">
            Access is restricted to authorized administrators only.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Project Management Dashboard</h1>
        <p>Manage project records that are shown on the Track Project page</p>
        <div className="admin-links">
          <Link to="/track-project">Open Track Project</Link>
          <Link to="/">Back Home</Link>
          <button className="secondary-button" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </div>

      <div className="admin-container">
        <div className="create-project-section">
          <h2>Create New Project</h2>
          <div className="project-form">
            <input
              type="text"
              placeholder="Project Name *"
              value={newProject.projectName}
              onChange={(e) => setNewProject({ ...newProject, projectName: e.target.value })}
              className="form-input"
            />
            <input
              type="text"
              placeholder="Client Name *"
              value={newProject.clientName}
              onChange={(e) => setNewProject({ ...newProject, clientName: e.target.value })}
              className="form-input"
            />
            <input
              type="email"
              placeholder="Client Email *"
              value={newProject.email}
              onChange={(e) => setNewProject({ ...newProject, email: e.target.value })}
              className="form-input"
            />
            <input
              type="date"
              value={newProject.startDate}
              onChange={(e) => setNewProject({ ...newProject, startDate: e.target.value })}
              className="form-input"
            />
            <input
              type="date"
              placeholder="Estimated Completion *"
              value={newProject.estimatedCompletion}
              onChange={(e) => setNewProject({ ...newProject, estimatedCompletion: e.target.value })}
              className="form-input"
            />
            <button onClick={createProject} disabled={loading} className="primary-button">
              {loading ? "Creating..." : "Create Project"}
            </button>
          </div>
        </div>

        <div className="projects-list-section">
          <h2>Existing Projects</h2>
          {loading ? (
            <div className="loading">Loading projects...</div>
          ) : (
            <div className="projects-grid">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className={`project-card ${selectedProject?.id === project.id ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedProject(project);
                    setEditForm({
                      clientName: project.clientName,
                      email: project.email,
                      projectName: project.projectName,
                      startDate: project.startDate || "",
                      estimatedCompletion: project.estimatedCompletion || "",
                      currentStep: project.currentStep || 1,
                      progress: project.progress || 0,
                    });
                  }}
                >
                  <div className="project-card-header">
                    <h3>{project.projectName}</h3>
                    <span className="project-id">ID: {project.id}</span>
                  </div>
                  <div className="project-card-body">
                    <p><strong>Client:</strong> {project.clientName}</p>
                    <p><strong>Email:</strong> {project.email}</p>
                    <p><strong>Status:</strong> {project.currentStepName || `Step ${project.currentStep}`}</p>
                    <p><strong>Progress:</strong> {project.progress}%</p>
                  </div>
                  <button
                    className="delete-project-btn"
                    onClick={(e) => { e.stopPropagation(); deleteProject(project.id); }}
                    disabled={loading}
                  >
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedProject && editForm && (
          <div className="update-status-section">
            <h2>Edit Project: {selectedProject.projectName}</h2>
            <div className="project-form">
              <input
                type="text"
                placeholder="Project Name *"
                value={editForm.projectName}
                onChange={(e) => setEditForm({ ...editForm, projectName: e.target.value })}
                className="form-input"
              />
              <input
                type="text"
                placeholder="Client Name *"
                value={editForm.clientName}
                onChange={(e) => setEditForm({ ...editForm, clientName: e.target.value })}
                className="form-input"
              />
              <input
                type="email"
                placeholder="Client Email *"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                className="form-input"
              />
              <input
                type="date"
                value={editForm.startDate}
                onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
                className="form-input"
              />
              <input
                type="date"
                placeholder="Estimated Completion *"
                value={editForm.estimatedCompletion}
                onChange={(e) => setEditForm({ ...editForm, estimatedCompletion: e.target.value })}
                className="form-input"
              />
              <div className="form-group">
                <label>Current Step:</label>
                <select
                  value={editForm.currentStep}
                  onChange={(e) => setEditForm({ ...editForm, currentStep: parseInt(e.target.value, 10) })}
                  className="form-select"
                >
                  {[["1","Discovery"],["2","Design"],["3","Development"],["4","Testing"],["5","Launch"],["6","Completed"]].map(([val, label]) => (
                    <option key={val} value={val}>Step {val} — {label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Progress: {editForm.progress}%</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={editForm.progress}
                  onChange={(e) => setEditForm({ ...editForm, progress: parseInt(e.target.value, 10) })}
                  className="progress-slider"
                />
              </div>
              <div style={{ display: "flex", gap: "1rem" }}>
                <button onClick={updateProject} disabled={loading} className="primary-button">
                  {loading ? "Saving..." : "Save Changes"}
                </button>
                <button
                  onClick={() => { setSelectedProject(null); setEditForm(null); }}
                  className="secondary-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="newsletter-admin-section">
          <div className="newsletter-admin-header">
            <h2>Newsletter Broadcast</h2>
            <p>
              Send a newsletter email to all subscribers currently stored from
              website sign-ups.
            </p>
          </div>

          <div className="newsletter-stats">
            <div className="newsletter-count-card">
              <span className="count-label">Subscribers</span>
              <span className="count-value">{subscribers.length}</span>
            </div>
            <button className="secondary-button" onClick={loadSubscribers}>
              Refresh List
            </button>
            <button className="secondary-button" onClick={copySubscribers}>
              Copy Emails
            </button>
          </div>

          <div className="newsletter-form-grid">
            <input
              type="text"
              placeholder="Newsletter Subject *"
              className="form-input"
              value={newsletterSubject}
              onChange={(e) => setNewsletterSubject(e.target.value)}
            />
            <textarea
              placeholder="Newsletter Message *"
              className="form-textarea newsletter-message"
              rows={8}
              value={newsletterMessage}
              onChange={(e) => setNewsletterMessage(e.target.value)}
            />
            <button
              className="primary-button"
              onClick={sendNewsletterToAll}
              disabled={!subscribers.length}
            >
              Send to All Subscribers
            </button>
          </div>

          <div className="subscriber-list">
            {subscribers.length === 0 ? (
              <p className="empty-subscribers">No subscribers yet.</p>
            ) : (
              subscribers.map((subscriber, index) => (
                <div key={`${subscriber.email}-${index}`} className="subscriber-row">
                  <span className="subscriber-email">{subscriber.email}</span>
                  <span className="subscriber-date">
                    {subscriber.subscribedAt
                      ? new Date(subscriber.subscribedAt).toLocaleString()
                      : "Date unavailable"}
                  </span>
                  <button
                    className="subscriber-remove"
                    onClick={() => removeSubscriber(subscriber.email)}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="testimonials-admin-section">
          <div className="testimonials-admin-header">
            <h2>Homepage Testimonials</h2>
            <p>Add, edit, or remove client testimonials shown on the home page.</p>
          </div>

          <form className="testimonials-form" onSubmit={handleTestimonialSubmit}>
            <textarea
              className="form-textarea"
              rows={4}
              placeholder="Testimonial content *"
              value={testimonialForm.content}
              onChange={(e) => setTestimonialForm({ ...testimonialForm, content: e.target.value })}
              required
            />
            <div className="testimonials-form-row">
              <input
                type="text"
                className="form-input"
                placeholder="Author name *"
                value={testimonialForm.author}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, author: e.target.value })}
                required
              />
              <input
                type="text"
                className="form-input"
                placeholder="Position / Company *"
                value={testimonialForm.position}
                onChange={(e) => setTestimonialForm({ ...testimonialForm, position: e.target.value })}
                required
              />
            </div>
            <input
              type="url"
              className="form-input"
              placeholder="Avatar image URL (optional)"
              value={testimonialForm.avatar}
              onChange={(e) => setTestimonialForm({ ...testimonialForm, avatar: e.target.value })}
            />
            <div className="testimonials-form-actions">
              <button type="submit" className="primary-button">
                {editingTestimonial !== null ? "Save Changes" : "Add Testimonial"}
              </button>
              {editingTestimonial !== null && (
                <button type="button" className="secondary-button" onClick={resetTestimonialForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="testimonials-list">
            {testimonials.length === 0 ? (
              <p className="empty-subscribers">No testimonials yet.</p>
            ) : (
              testimonials.map((item, index) => (
                <div key={`${item.author}-${index}`} className="testimonial-row">
                  <div className="testimonial-meta">
                    <div className="testimonial-header">
                      <strong>{item.author}</strong>
                      <span>{item.position}</span>
                    </div>
                    <p className="testimonial-text">{item.content}</p>
                  </div>
                  <div className="testimonial-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => startEditTestimonial(index)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="subscriber-remove"
                      onClick={() => removeTestimonialEntry(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="faqs-admin-section">
          <div className="faqs-admin-header">
            <h2>Homepage FAQs</h2>
            <p>Add, edit, or remove the FAQs shown on the home page.</p>
          </div>

          <form className="faqs-form" onSubmit={handleFaqSubmit}>
            <input
              type="text"
              className="form-input"
              placeholder="Question *"
              value={faqForm.question}
              onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
              required
            />
            <textarea
              className="form-textarea"
              rows={4}
              placeholder="Answer *"
              value={faqForm.answer}
              onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
              required
            />
            <div className="testimonials-form-actions">
              <button type="submit" className="primary-button">
                {editingFaq !== null ? "Save Changes" : "Add FAQ"}
              </button>
              {editingFaq !== null && (
                <button type="button" className="secondary-button" onClick={resetFaqForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div className="faqs-list">
            {faqs.length === 0 ? (
              <p className="empty-subscribers">No FAQs yet.</p>
            ) : (
              faqs.map((item, index) => (
                <div key={`${item.question}-${index}`} className="faq-row">
                  <div className="testimonial-meta">
                    <div className="testimonial-header">
                      <strong>{item.question}</strong>
                    </div>
                    <p className="testimonial-text">{item.answer}</p>
                  </div>
                  <div className="testimonial-actions">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => startEditFaq(index)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="subscriber-remove"
                      onClick={() => removeFaqEntry(index)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
